import { faBolt, faLock } from '@fortawesome/pro-regular-svg-icons';
import {
  faChevronDown,
  faChevronUp,
  faEllipsisV
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Button,
  Chip,
  IconButton,
  Stack,
  Tooltip,
  Typography
} from '@mui/material';
import type { ICellRendererParams } from 'ag-grid-enterprise';
import { uniq } from 'lodash';
import { useEffect, useState } from 'react';

import CheckPermissions from '@/components/CheckPermissions';
import { useContextMenu } from '@/components/ContextMenu';
import PERMISSIONS from '@/constants/auth.constants';
import { PERMISSIONS_LOGIC } from '@/constants/common';
import {
  ORGANIZATIONS_CONTEXT_MENU_ITEMS,
  ORGANIZATIONS_CONTEXT_MENU_ITEMS_IR
} from '@/pages/Organizations';
import type { Organization } from '@/pages/Organizations/Organizations.types';

export const OrgExpandCellRenderer = ({
  node
}: ICellRendererParams<Organization>) => {
  const [isExpanded, setIsExpanded] = useState(node.expanded);

  useEffect(() => {
    const handleExpandedChanged = () => setIsExpanded(node.expanded);
    node.addEventListener('expandedChanged', handleExpandedChanged);
    return () =>
      node.removeEventListener('expandedChanged', handleExpandedChanged);
  }, [node]);

  if (!node.master) return null;

  return (
    <IconButton size="small" onClick={() => node.setExpanded(!node.expanded)}>
      <FontAwesomeIcon
        icon={isExpanded ? faChevronUp : faChevronDown}
        size="xs"
      />
    </IconButton>
  );
};

export const OrgNameCellRenderer = ({
  data
}: ICellRendererParams<Organization>) => {
  if (!data) return null;
  return (
    <Stack
      direction="row"
      sx={{ gap: 1, alignItems: 'center', flexWrap: 'wrap' }}
    >
      <Typography sx={{ flex: 1 }}>{data.name}</Typography>
      {!!data.is_auto_soc && (
        <Tooltip title="Auto SOC" placement="top">
          <FontAwesomeIcon icon={faBolt} />
        </Tooltip>
      )}
      {data.is_restricted && (
        <Tooltip title="Restricted Access" placement="top">
          <FontAwesomeIcon icon={faLock} />
        </Tooltip>
      )}
      {data.label?.includes('demo') && (
        <Chip color="info" label="Demo" size="small" />
      )}
    </Stack>
  );
};

export const OrgProductTypeCellRenderer = ({
  data
}: ICellRendererParams<Organization>) => {
  if (!data) return null;
  return (
    <Stack sx={{ flexDirection: 'row', gap: 0.5, flexWrap: 'wrap' }}>
      {uniq(data.product_type || [])
        .sort()
        .map((type) => (
          <Chip label={type} key={type} size="small" />
        ))}
    </Stack>
  );
};

export const OrgProductStatusCellRenderer = ({
  data
}: ICellRendererParams<Organization>) => {
  if (!data) return null;
  return (
    <>
      {data.product_status === 'Expired' && (
        <Chip color="error" label="Expired" variant="outlined" size="small" />
      )}
      {data.product_status === 'Expiring Soon' && (
        <Chip
          color="warning"
          label="Expiring Soon"
          variant="outlined"
          size="small"
        />
      )}
    </>
  );
};

export const OrgCountCellRenderer = ({
  value
}: ICellRendererParams<Organization>) =>
  value != null ? <Chip variant="filled" label={value} size="small" /> : null;

export const OrgActionCellRenderer = ({
  data
}: ICellRendererParams<Organization>) => {
  const { showContextMenu } = useContextMenu();
  if (!data) return null;
  return (
    <CheckPermissions
      permissions={[
        PERMISSIONS.ORGANIZATIONS_UPDATE,
        PERMISSIONS.ORGANIZATIONS_DELETE
      ]}
      logic={PERMISSIONS_LOGIC.OR}
    >
      <Button
        variant="text"
        onClick={(e) =>
          showContextMenu(
            e,
            `${data.id}`,
            data.is_ir
              ? ORGANIZATIONS_CONTEXT_MENU_ITEMS_IR
              : ORGANIZATIONS_CONTEXT_MENU_ITEMS
          )
        }
      >
        <FontAwesomeIcon icon={faEllipsisV} />
      </Button>
    </CheckPermissions>
  );
};
